import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Upload as UploadIcon, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertCircle,
  X,
  FileUp,
  ArrowRight,
  Cloud,
  HardDrive,
  RefreshCw,
  Settings,
  Link as LinkIcon,
  Table,
  Star,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { TurnstileWidget } from "../components/TurnstileWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SyncHistorySection() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/admin/ms365/sync-history?limit=20`);
        setHistory(res.data?.history || []);
      } catch {
        // Silently fail - non-critical
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const formatTime = (ts) => {
    if (!ts) return "N/A";
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  if (loading) return null;
  if (history.length === 0) return null;

  return (
    <Card data-testid="sync-history-section">
      <CardHeader>
        <CardTitle className="text-base">Sync History</CardTitle>
        <CardDescription>Last {history.length} synchronization operations</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b text-left">
                <th className="pb-2 font-medium">Time</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium">Triggered By</th>
                <th className="pb-2 font-medium">Records</th>
                <th className="pb-2 font-medium">Errors</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={h.id || i} className="border-b border-border/50">
                  <td className="py-1.5 text-xs">{formatTime(h.started_at)}</td>
                  <td className="py-1.5">
                    <Badge 
                      variant={h.status === "success" ? "default" : h.status === "partial" ? "secondary" : "destructive"}
                      className="text-xs"
                    >
                      {h.status}
                    </Badge>
                  </td>
                  <td className="py-1.5 text-xs">{h.triggered_by || "System"}</td>
                  <td className="py-1.5 text-xs">{h.timesheet_result?.count || 0} timesheets, {h.master_result?.count || 0} projects</td>
                  <td className="py-1.5 text-xs">
                    {h.errors?.length > 0 ? (
                      <span className="text-destructive">{h.errors.length} error(s)</span>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DataSource() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  
  // Sheet selection
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [showSheetSelector, setShowSheetSelector] = useState(false);
  
  const [dataSource, setDataSource] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Project master upload
  const [masterFile, setMasterFile] = useState(null);
  const [masterUploading, setMasterUploading] = useState(false);
  const [masterUploadResult, setMasterUploadResult] = useState(null);
  const [masterSheets, setMasterSheets] = useState([]);
  const [selectedMasterSheet, setSelectedMasterSheet] = useState(null);
  const [masterAnalyzing, setMasterAnalyzing] = useState(false);
  
  // MS365 status
  const [ms365Configured, setMs365Configured] = useState(false);
  const [ms365Connected, setMs365Connected] = useState(false);
  const [ms365StatusDetails, setMs365StatusDetails] = useState({});
  const [ms365Links, setMs365Links] = useState({ timesheet_url: "", timesheet_sheet: "", master_url: "", master_sheet: "" });
  const [savingMs365Links, setSavingMs365Links] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [tsSheets, setTsSheets] = useState([]);
  const [detectingSheets, setDetectingSheets] = useState({ ts: false, master: false });

  useEffect(() => {
    fetchDataSource();
    fetchSyncStatus();
    // Check MS365 server config and actual connection status
    axios.get(`${API}/admin/ms365/config`).then(res => setMs365Configured(res.data?.configured || false)).catch(() => {});
    axios.get(`${API}/admin/ms365/status`).then(res => {
      setMs365Connected(res.data?.connected || false);
      setMs365StatusDetails(res.data || {});
    }).catch(() => {});
    axios.get(`${API}/admin/ms365/file-links`).then(res => setMs365Links(res.data)).catch(() => {});
  }, []);

  const fetchDataSource = async () => {
    try { const response = await axios.get(`${API}/data-source`); setDataSource(response.data); }
    catch (error) { console.error("Error:", error); }
  };

  const fetchSyncStatus = async () => {
    try { 
      const response = await axios.get(`${API}/sync/status`); 
      setSyncStatus(response.data);
      if (response.data?.results) setSyncResult(response.data.results);
    }
    catch (error) { console.error("Error:", error); }
  };

  const handleSaveMs365Links = async () => {
    try {
      setSavingMs365Links(true);
      await axios.post(`${API}/admin/ms365/file-links`, ms365Links);
      toast.success("SharePoint file links saved");
    } catch (error) {
      toast.error("Failed to save file links");
    } finally {
      setSavingMs365Links(false);
    }
  };

  const handleMs365Sync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      await handleSaveMs365Links(); // save before sync just in case
      const response = await axios.post(`${API}/admin/ms365/sync`);
      toast.success("MS365 sync completed");
      setSyncResult(response.data.results);
      fetchDataSource();
      fetchSyncStatus();
    } catch (error) {
      const msg = error.response?.data?.detail || "Sync failed";
      toast.error(msg);
      if (error.response?.status === 401) {
        // Token auth error
        navigate("/settings");
      }
    } finally {
      setSyncing(false);
    }
  };

  // File upload handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        processFile(droppedFile);
      }
    }
  }, []);

  const validateFile = (file) => {
    const validExtensions = [".xlsx", ".xls", ".csv"];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!hasValidExtension) {
      toast.error("Please upload an Excel (.xlsx, .xls) or CSV file");
      return false;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return false;
    }

    return true;
  };

  const processFile = async (selectedFile) => {
    setFile(selectedFile);
    setUploadResult(null);
    setSheets([]);
    setSelectedSheet(null);
    setShowSheetSelector(false);
    
    // For Excel files, analyze sheets first
    if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
      setAnalyzing(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      try {
        const response = await axios.post(`${API}/upload/analyze`, formData, {
          headers: { "Content-Type": "multipart/form-data", "X-Turnstile-Token": turnstileToken || "" }
        });
        
        const sheetsData = response.data.sheets || [];
        setSheets(sheetsData);
        
        // Store file content for later upload
        const reader = new FileReader();
        reader.onload = (e) => setFileContent(e.target.result);
        reader.readAsArrayBuffer(selectedFile);
        
        if (sheetsData.length > 1) {
          // Multiple sheets - show selector
          setShowSheetSelector(true);
          // Auto-select recommended sheet
          const recommended = sheetsData.find(s => s.recommended);
          if (recommended) {
            setSelectedSheet(recommended.name);
          }
        } else if (sheetsData.length === 1) {
          // Single sheet - auto-select
          setSelectedSheet(sheetsData[0].name);
        }
      } catch (error) {
        toast.error("Failed to analyze file");
        console.error(error);
      } finally {
        setAnalyzing(false);
      }
    } else {
      // CSV file - no sheet selection needed
      setSelectedSheet("CSV Data");
    }
  };

  const analyzeMasterFile = async (selectedFile) => {
    setMasterSheets([]);
    setSelectedMasterSheet(null);
    setMasterAnalyzing(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      // Use master-specific analyze endpoint so sheet recommendation is based on master columns
      const response = await axios.post(`${API}/admin/projects-master/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data", "X-Turnstile-Token": turnstileToken || "" },
      });
      const sheetsData = response.data.sheets || [];
      setMasterSheets(sheetsData);

      if (sheetsData.length > 0) {
        const recommended = sheetsData.find((s) => s.recommended);
        // default to recommended else first
        setSelectedMasterSheet((recommended?.name || sheetsData[0]?.name) ?? null);
      }
    } catch (error) {
      console.error("Failed to analyze master file", error);
      toast.error("Failed to analyze project master file");
    } finally {
      setMasterAnalyzing(false);
    }
  };
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        processFile(selectedFile);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    
    // Add sheet_name if Excel file
    const url = selectedSheet && selectedSheet !== "CSV Data" 
      ? `${API}/upload?sheet_name=${encodeURIComponent(selectedSheet)}`
      : `${API}/upload`;

    try {
      const response = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data", "X-Turnstile-Token": turnstileToken || "" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setUploadResult({ success: true, message: response.data.message, count: response.data.count });
      toast.success(`Successfully uploaded ${response.data.count} entries from "${selectedSheet}"`);
      fetchDataSource();
      setShowSheetSelector(false);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to upload file";
      setUploadResult({ success: false, message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileContent(null);
    setUploadResult(null);
    setUploadProgress(0);
    setSheets([]);
    setSelectedSheet(null);
    setShowSheetSelector(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="data-source-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Data Source</h1>
        <p className="text-muted-foreground mt-2">
          Connect to Microsoft 365 SharePoint or upload files from your computer
        </p>
      </div>

      {/* Current Status */}
      {(dataSource?.source_type || syncStatus?.last_sync) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Current Data Source</CardTitle>
              {syncStatus?.source_type === "ms365" && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={syncing}
                  className="gap-2"
                  data-testid="sync-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing..." : "Sync Now"}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${dataSource?.source_type === "ms365" ? "bg-blue-500/10" : "bg-emerald-500/10"}`}>
                {dataSource?.source_type === "ms365" ? (
                  <Cloud className="w-6 h-6 text-blue-500" />
                ) : (
                  <HardDrive className="w-6 h-6 text-emerald-500" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">
                  {dataSource?.source_type === "ms365" ? "Microsoft 365 SharePoint" : "Local File Upload"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {dataSource?.file_name || syncStatus?.file_name || "No file selected"}
                  {dataSource?.sheet_name && ` → ${dataSource.sheet_name}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">
                  {dataSource?.records_count || syncStatus?.records_synced || 0} records
                </p>
                <p className="text-xs text-muted-foreground">
                  Last updated: {dataSource?.last_updated ? new Date(dataSource.last_updated).toLocaleString() : "Never"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Source Options */}
      <Tabs defaultValue="upload" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload" className="gap-2" data-testid="upload-tab">
            <HardDrive className="w-4 h-4" />
            Timesheet Upload
          </TabsTrigger>
          <TabsTrigger value="master" className="gap-2" data-testid="master-tab">
            <FileSpreadsheet className="w-4 h-4" />
            Project Master
          </TabsTrigger>
          <TabsTrigger value="ms365" className="gap-2" data-testid="ms365-tab">
            <Cloud className="w-4 h-4" />
            Microsoft 365
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Upload from Computer</CardTitle>
              <CardDescription>
                Drag and drop your Excel or CSV file, or click to browse
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Turnstile CAPTCHA — required for all file uploads */}
              <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />
              {!file ? (
                <div
                  className={`upload-zone ${dragActive ? "dragging" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  data-testid="upload-dropzone"
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileInputChange}
                    data-testid="file-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <FileUp className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-lg font-medium text-foreground mb-2">
                        Drop your file here
                      </p>
                      <p className="text-sm text-muted-foreground mb-4">
                        or click to browse
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Supports Excel (.xlsx, .xls) and CSV files up to 10MB
                      </p>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* File preview */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-emerald-500" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                          {sheets.length > 1 && ` • ${sheets.length} sheets`}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={clearFile} disabled={uploading || analyzing} data-testid="clear-file-btn">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Analyzing indicator */}
                  {analyzing && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-500/10 text-blue-700 dark:text-blue-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Analyzing file sheets...</span>
                    </div>
                  )}

                  {/* Sheet Selection */}
                  {showSheetSelector && sheets.length > 1 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Table className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">Select Sheet to Import</span>
                      </div>
                      <div className="grid gap-2 max-h-64 overflow-y-auto">
                        {sheets.map((sheet) => (
                          <button
                            key={sheet.name}
                            onClick={() => setSelectedSheet(sheet.name)}
                            className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                              selectedSheet === sheet.name 
                                ? "border-primary bg-primary/5" 
                                : "border-border hover:border-primary/50 hover:bg-muted/50"
                            }`}
                            data-testid={`sheet-${sheet.name}`}
                          >
                            <div className="flex items-center gap-3">
                              <Table className="w-5 h-5 text-muted-foreground" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{sheet.name}</span>
                                  {sheet.recommended && (
                                    <Badge variant="secondary" className="gap-1 text-xs">
                                      <Star className="w-3 h-3" />
                                      Recommended
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {sheet.rows} rows • {sheet.columns?.length || 0} columns
                                  {sheet.matched_columns?.length > 0 && (
                                    <span className="text-emerald-600 dark:text-emerald-400">
                                      {" "}• Matches: {sheet.matched_columns.join(", ")}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {selectedSheet === sheet.name && (
                              <CheckCircle2 className="w-5 h-5 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Single sheet info */}
                  {!showSheetSelector && sheets.length === 1 && sheets[0] && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                      <Table className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {sheets[0].rows} rows • {sheets[0].columns?.length || 0} columns
                      </span>
                    </div>
                  )}

                  {/* Upload progress */}
                  {uploading && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Uploading...</span>
                        <span className="font-medium">{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="h-2" />
                    </div>
                  )}

                  {/* Upload result */}
                  {uploadResult && (
                    <div 
                      className={`flex items-center gap-3 p-4 rounded-lg ${
                        uploadResult.success 
                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" 
                          : "bg-red-500/10 text-red-700 dark:text-red-400"
                      }`}
                      data-testid="upload-result"
                    >
                      {uploadResult.success ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                      <div>
                        <p className="font-medium">{uploadResult.message}</p>
                        {uploadResult.count && (
                          <p className="text-sm opacity-80">{uploadResult.count} timesheet entries imported</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-2">
                    {!uploadResult?.success ? (
                      <Button 
                        onClick={handleUpload} 
                        disabled={uploading || analyzing || !selectedSheet || !turnstileToken} 
                        className="gap-2" 
                        data-testid="upload-btn"
                      >
                        <UploadIcon className="w-4 h-4" />
                        {uploading ? "Uploading..." : `Import ${selectedSheet || "Data"}`}
                      </Button>
                    ) : (
                      <Button onClick={() => navigate("/")} className="gap-2" data-testid="view-dashboard-btn">
                        View Dashboard
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    )}
                    {!uploading && !analyzing && (
                      <Button variant="outline" onClick={clearFile} data-testid="choose-different-btn">
                        Choose Different File
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="master">
          {/* Project Master Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Project Master File (Budgets &amp; Metadata)</CardTitle>
              <CardDescription>
                Upload a separate Excel/CSV file containing project numbers, client, status, project value and quoted hours. This data is merged with the timesheet projects for budgeting &amp; profitability.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const f = e.target.files[0];
                        setMasterFile(f);
                        setMasterUploadResult(null);
                        setMasterSheets([]);
                        setSelectedMasterSheet(null);
                        if (f.name.endsWith(".xlsx") || f.name.endsWith(".xls")) {
                          analyzeMasterFile(f);
                        }
                      }
                    }}
                    className="text-xs"
                    data-testid="master-file-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!masterFile || masterUploading}
                    onClick={async () => {
                      if (!masterFile) return;
                      try {
                        setMasterUploading(true);
                        const formData = new FormData();
                        formData.append("file", masterFile);
                        let url = `${API}/admin/projects-master/upload`;
                        if (selectedMasterSheet) {
                          url += `?sheet_name=${encodeURIComponent(selectedMasterSheet)}`;
                        }
                        const response = await axios.post(url, formData, {
                          headers: { "Content-Type": "multipart/form-data", "X-Turnstile-Token": turnstileToken || "" },
                        });
                        setMasterUploadResult({
                          success: true,
                          message: response.data.message,
                          count: response.data.count,
                          sheet: response.data.sheet,
                          skipped: response.data.skipped,
                          skip_reasons: response.data.skip_reasons,
                        });
                        toast.success(response.data.message || "Project master uploaded");
                        // refresh the overall data source status so admin dashboard reflects new master upload
                        fetchDataSource();
                      } catch (error) {
                        const msg = error.response?.data?.detail || "Failed to upload project master";
                        setMasterUploadResult({ success: false, message: msg });
                        toast.error(msg);
                      } finally {
                        setMasterUploading(false);
                      }
                    }}
                    data-testid="master-upload-btn"
                  >
                    {masterUploading ? "Uploading..." : "Upload Project Master"}
                  </Button>
                </div>

                {masterAnalyzing && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing sheets...
                  </div>
                )}
              </div>

              {masterSheets.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Select sheet:</span>
                    <select
                      className="border border-border rounded px-2 py-1 bg-background"
                      value={selectedMasterSheet || ""}
                      onChange={(e) => setSelectedMasterSheet(e.target.value || null)}
                      data-testid="master-sheet-select"
                    >
                      <option value="">Auto (recommended)</option>
                      {masterSheets.map((s) => (
                        <option key={s.name} value={s.name}>
                          {s.name}{s.recommended ? " (recommended)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {masterSheets.length > 1 && (
                    <div className="text-xs text-muted-foreground">
                      Available sheets: {masterSheets.map((s) => s.name).join(", ")}
                    </div>
                  )}
                </div>
              )}

              {masterUploadResult && (
                <div className="space-y-1" data-testid="master-upload-result">
                  <p className={`text-xs ${masterUploadResult.success ? "text-emerald-600" : "text-red-600"}`}>
                    {masterUploadResult.message}
                  </p>
                  {masterUploadResult.success && typeof masterUploadResult.count === "number" && (
                    <p className="text-xs text-muted-foreground">
                      Imported: {masterUploadResult.count} projects
                      {typeof masterUploadResult.skipped === "number" && ` • Skipped: ${masterUploadResult.skipped}`}
                      {masterUploadResult.sheet ? ` • Sheet: ${masterUploadResult.sheet}` : ""}
                    </p>
                  )}
                  {masterUploadResult.success && Array.isArray(masterUploadResult.skip_reasons) && masterUploadResult.skip_reasons.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Top skip reasons: {masterUploadResult.skip_reasons.map((r) => `${r.reason} (${r.count})`).join(", ")}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MS365 Tab */}
        <TabsContent value="ms365">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Microsoft 365 SharePoint</CardTitle>
              <CardDescription>Connect to SharePoint to sync timesheet and master files automatically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!ms365Configured ? (
                <div className="text-center py-8">
                  <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Configure Microsoft 365</h3>
                  <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                    Set up your Azure AD credentials in Settings to enable SharePoint sync
                  </p>
                  <Button onClick={() => navigate("/settings")} className="gap-2" data-testid="go-to-settings-btn">
                    <Settings className="w-4 h-4" /> Go to Settings
                  </Button>
                </div>
              ) : !ms365Connected ? (
                <div className="text-center py-8">
                  <Cloud className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Verify Microsoft 365 Connection</h3>
                  <p className="text-muted-foreground mb-2 max-w-md mx-auto">
                    Credentials are saved. Click Test Connection to verify they work.
                  </p>
                  {ms365StatusDetails?.error && (
                    <p className="text-red-500 text-sm mb-4 max-w-md mx-auto">{ms365StatusDetails.error}</p>
                  )}
                  <div className="flex gap-2 justify-center">
                    <Button onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/admin/ms365/test`);
                        if (res.data.success) {
                          toast.success(res.data.message);
                          setMs365Connected(true);
                        } else {
                          toast.error(res.data.message);
                        }
                      } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
                    }} className="gap-2 bg-blue-600 hover:bg-blue-700">
                      <Cloud className="w-4 h-4" /> Test Connection
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/settings")}>Edit Credentials</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className="font-medium text-foreground">Azure AD Configured</p>
                        <p className="text-sm text-muted-foreground">Credentials saved</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/admin/ms365/test`);
                          if (res.data.success) toast.success(res.data.message);
                          else toast.error(res.data.message);
                        } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
                      }}>Test Connection</Button>
                      <Button variant="ghost" size="sm" onClick={() => navigate("/settings")} className="text-xs">Settings</Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-4">
                      {/* Timesheet File Link */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Timesheet File SharePoint URL</label>
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            value={ms365Links.timesheet_url}
                            onChange={(e) => setMs365Links(prev => ({ ...prev, timesheet_url: e.target.value }))}
                            placeholder="e.g. https://yourorg.sharepoint.com/:x:/s/Data/Ed_... (Share Link)"
                            className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                          />
                          {tsSheets.length > 0 ? (
                            <select className="w-40 h-10 px-3 rounded-md border border-input bg-background text-sm" value={ms365Links.timesheet_sheet} onChange={(e) => setMs365Links(prev => ({ ...prev, timesheet_sheet: e.target.value }))}>
                              <option value="">Select sheet...</option>
                              {tsSheets.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          ) : (
                            <input type="text" value={ms365Links.timesheet_sheet} onChange={(e) => setMs365Links(prev => ({ ...prev, timesheet_sheet: e.target.value }))} placeholder="Sheet" className="w-32 h-10 px-3 rounded-md border border-input bg-background text-sm" />
                          )}
                          <Button variant="outline" size="sm" className="h-10" disabled={!ms365Links.timesheet_url || detectingSheets.ts} onClick={async () => {
                            setDetectingSheets(p => ({ ...p, ts: true }));
                            try {
                              const res = await axios.post(`${API}/admin/ms365/detect-sheets`, { url: ms365Links.timesheet_url });
                              setTsSheets(res.data.sheets || []);
                              if (res.data.recommended) setMs365Links(prev => ({ ...prev, timesheet_sheet: res.data.recommended }));
                              toast.success(`Found ${res.data.sheets?.length || 0} sheets`);
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                            finally { setDetectingSheets(p => ({ ...p, ts: false })); }
                          }}>{detectingSheets.ts ? "..." : "Detect"}</Button>
                        </div>
                        <p className="text-xs text-muted-foreground">Paste the "Copy link" URL from SharePoint or OneDrive for your timesheet Excel/CSV file.</p>
                      </div>
                      
                      {/* Master File Link */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Project Master File SharePoint URL</label>
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            value={ms365Links.master_url}
                            onChange={(e) => setMs365Links(prev => ({ ...prev, master_url: e.target.value }))}
                            placeholder="e.g. https://yourorg.sharepoint.com/:x:/s/Data/Ed_..."
                            className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                          />
                          {masterSheets.length > 0 ? (
                            <select className="w-40 h-10 px-3 rounded-md border border-input bg-background text-sm" value={ms365Links.master_sheet} onChange={(e) => setMs365Links(prev => ({ ...prev, master_sheet: e.target.value }))}>
                              <option value="">Select sheet...</option>
                              {masterSheets.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          ) : (
                            <input type="text" value={ms365Links.master_sheet} onChange={(e) => setMs365Links(prev => ({ ...prev, master_sheet: e.target.value }))} placeholder="Sheet" className="w-32 h-10 px-3 rounded-md border border-input bg-background text-sm" />
                          )}
                          <Button variant="outline" size="sm" className="h-10" disabled={!ms365Links.master_url || detectingSheets.master} onClick={async () => {
                            setDetectingSheets(p => ({ ...p, master: true }));
                            try {
                              const res = await axios.post(`${API}/admin/ms365/detect-sheets`, { url: ms365Links.master_url });
                              setMasterSheets(res.data.sheets || []);
                              if (res.data.recommended) setMs365Links(prev => ({ ...prev, master_sheet: res.data.recommended }));
                              toast.success(`Found ${res.data.sheets?.length || 0} sheets`);
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                            finally { setDetectingSheets(p => ({ ...p, master: false })); }
                          }}>{detectingSheets.master ? "..." : "Detect"}</Button>
                        </div>
                        <p className="text-xs text-muted-foreground">Paste the URL for your project master/budget file.</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <Button onClick={handleMs365Sync} disabled={syncing || (!ms365Links.timesheet_url && !ms365Links.master_url)} className="gap-2">
                        <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                        {syncing ? "Syncing..." : "Save & Sync Now"}
                      </Button>
                      <Button variant="outline" onClick={handleSaveMs365Links} disabled={savingMs365Links}>
                        {savingMs365Links ? "Saving..." : "Save Links Only"}
                      </Button>
                    </div>

                    {/* Auto Sync Config */}
                    <div className="p-4 rounded-lg border border-border mt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Auto Sync</p>
                          <p className="text-xs text-muted-foreground">Automatically sync SharePoint files at regular intervals</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="number" min="1" max="1440" defaultValue="30" id="auto-sync-interval" className="w-16 h-8 text-xs text-center border border-border rounded-md bg-background" />
                          <span className="text-xs text-muted-foreground">min</span>
                          <Button variant="outline" size="sm" onClick={async () => {
                            const interval = parseInt(document.getElementById("auto-sync-interval")?.value || "30");
                            try {
                              const res = await axios.post(`${API}/admin/ms365/auto-sync`, { enabled: true, interval_minutes: interval });
                              toast.success(res.data.message);
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          }}>Enable</Button>
                          <Button variant="ghost" size="sm" onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/ms365/auto-sync`, { enabled: false });
                              toast.success("Auto-sync disabled");
                            } catch { toast.error("Failed"); }
                          }}>Disable</Button>
                        </div>
                      </div>
                    </div>

                    {/* Sync Results Warning/Success */}
                    {syncResult && (
                      <div className="p-4 rounded-lg bg-muted border mt-4 text-sm space-y-2">
                        <p className="font-semibold text-foreground">Last Sync Results</p>
                        
                        {/* Timesheet Result */}
                        {ms365Links.timesheet_url && (
                          <div className="flex items-start gap-2">
                            {syncResult.timesheet_error ? (
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                            ) : syncResult.timesheet ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                            ) : (
                              <Loader2 className="w-4 h-4 text-muted-foreground mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium">Timesheet</p>
                              {syncResult.timesheet_error ? (
                                <p className="text-red-600 text-xs">{syncResult.timesheet_error}</p>
                              ) : syncResult.timesheet ? (
                                <p className="text-muted-foreground text-xs">Imported {syncResult.timesheet.count} records from sheet "{syncResult.timesheet.sheet || 'Auto'}"</p>
                              ) : <p className="text-muted-foreground text-xs">No result</p>}
                            </div>
                          </div>
                        )}

                        {/* Master Result */}
                        {ms365Links.master_url && (
                          <div className="flex items-start gap-2 mt-2">
                            {syncResult.master_error ? (
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                            ) : syncResult.master ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                            ) : (
                              <Loader2 className="w-4 h-4 text-muted-foreground mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium">Project Master</p>
                              {syncResult.master_error ? (
                                <p className="text-red-600 text-xs">{syncResult.master_error}</p>
                              ) : syncResult.master ? (
                                <p className="text-muted-foreground text-xs">Imported {syncResult.master.count} projects (Skipped {syncResult.master.skipped})</p>
                              ) : <p className="text-muted-foreground text-xs">No result</p>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Expected Format Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Expected File Format</CardTitle>
          <CardDescription>Your file should contain these columns (we&apos;ll auto-detect the best sheet)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 font-medium text-foreground">Column</th>
                  <th className="text-left py-2 px-3 font-medium text-foreground">Example</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border">
                  <td className="py-2 px-3 font-medium text-foreground">Personnel</td>
                  <td className="py-2 px-3">John Halfacre</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 px-3 font-medium text-foreground">Project ID</td>
                  <td className="py-2 px-3">1-456-SpillConsult-NIAC-INT</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 px-3 font-medium text-foreground">Company</td>
                  <td className="py-2 px-3">SpillConsult</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2 px-3 font-medium text-foreground">Hours</td>
                  <td className="py-2 px-3">3.00</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-medium text-foreground">Fee Earning</td>
                  <td className="py-2 px-3">Fee Earning Prep / Non-Fee Earning Prep</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Sync History Section */}
      {user?.role === "admin" && <SyncHistorySection />}
    </div>
  );
}
